import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// VAPID public key - this is the publishable key
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

export function PushNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Check if already subscribed
    checkExistingSubscription();
  }, [user]);

  const checkExistingSubscription = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const pm = (registration as any).pushManager;
      if (!pm) return;
      const sub = await pm.getSubscription();
      setSubscription(sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    if (!user) {
      toast({
        title: 'Sign In Required',
        description: 'Please sign in to enable notifications.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast({
          title: 'Permission Denied',
          description: 'Please allow notifications to receive updates.',
          variant: 'destructive',
        });
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const convertedVapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const pm = (registration as any).pushManager;

      const pushSubscription = await pm.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });

      // Extract keys from subscription
      const subscriptionJson = pushSubscription.toJSON();
      const p256dh = subscriptionJson.keys?.p256dh || '';
      const auth = subscriptionJson.keys?.auth || '';

      // Save subscription to database
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: pushSubscription.endpoint,
          p256dh,
          auth,
        }, {
          onConflict: 'user_id,endpoint'
        });

      if (error) {
        console.error('Failed to save subscription:', error);
        throw new Error('Failed to save notification preferences');
      }

      setSubscription(pushSubscription);

      toast({
        title: 'Notifications Enabled',
        description: 'You will now receive smart daily updates.',
      });
    } catch (error: any) {
      console.error('Failed to subscribe to push notifications:', error);
      toast({
        title: 'Subscription Failed',
        description: error.message || 'Could not enable notifications. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setLoading(true);
    try {
      if (subscription) {
        await subscription.unsubscribe();

        // Remove from database
        if (user) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint);
        }

        setSubscription(null);
        toast({
          title: 'Notifications Disabled',
          description: 'You will no longer receive push notifications.',
        });
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to disable notifications.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  return (
    <div>
      {loading ? (
        <Button
          variant="ghost"
          size="icon"
          disabled
          className="hover:bg-surface-hover"
        >
          <Loader2 size={20} className="animate-spin text-muted-foreground" />
        </Button>
      ) : subscription ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={unsubscribeFromPush}
          title="Disable notifications"
          className="hover:bg-surface-hover"
        >
          <Bell size={20} className="text-primary" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={subscribeToPush}
          title="Enable notifications"
          className="hover:bg-surface-hover"
        >
          <BellOff size={20} className="text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
